import {
  buildingDashboardApi,
  ColumnSourceEnum,
  DashboardBuildingHotspotOUT,
  getLocalAuthHeader,
  mappingEditorDynamicColumnsFp,
  MappingEditorApiV1RoutersDynamicColumnsGetDynamicColumnsViewNameEnum,
} from "api-client";
import { makeAutoObservable, runInAction } from "mobx";
import { buildingStore } from "store/Building/BuildingStore";
import { SelectOption } from "components/DropdownSelect";

// The 1.2 card's GWP sub-indicator cells are fixed to these four indicators;
// their per-EPD totals are pulled from the hotspot endpoint (the keys must
// match the hotspot `value` / dynamic-column key_name).
const GWP_SUBINDICATOR_KEYS = [
  "match_calculations.gwp_total",
  "match_calculations.gwpfossil_total",
  "match_calculations.gwpbiogenic_total",
  "match_calculations.gwpluluc_total",
];

const DEFAULT_INDICATOR = "match_calculations.gwp_total";

/**
 * State for the Level(s) certification dashboard. The shared GWP/emissions data
 * (1.2 module breakdowns) still comes from BuildingDashboardStore; this store
 * owns the Level(s)-specific hotspot analysis and GWP sub-indicator totals.
 */
class LevelsDashboardStore {
  // Hotspot analysis — Top-N EPDs ranked by the selected indicator.
  hotspotInfo: DashboardBuildingHotspotOUT | null = null;
  // Indicators selectable in the ranking — fetched dynamically from the
  // HOTSPOT_ANALYSIS dynamic-columns view (seeded with a sensible default).
  hotspotValueOptions: SelectOption[] = [];
  hotspotValueSelected: string = DEFAULT_INDICATOR;
  readonly hotspotTopN: number = 10;

  // GWP sub-indicator totals (gwp/fossil/biogenic/luluc), keyed by indicator
  // value string. Sourced from the hotspot endpoint, so construction-scope only
  // (B6 excluded) — the card flags this.
  gwpIndicatorTotals: Record<string, number | null> = {};

  get currentBuildingID(): string | undefined {
    return buildingStore.currentBuilding?.id;
  }

  setHotspotValueSelected(newValue: string) {
    runInAction(() => {
      this.hotspotValueSelected = newValue;
      this.hotspotInfo = null; // drop stale ranking while the new one loads
      this.fetchHotspots(this.currentBuildingID);
    });
  }

  async fetchHotspotColumns(building_id: string | undefined): Promise<void> {
    if (!building_id) return;
    const columns = await mappingEditorDynamicColumnsFp()
      .mappingEditorApiV1RoutersDynamicColumnsGetDynamicColumns(
        undefined,
        building_id,
        MappingEditorApiV1RoutersDynamicColumnsGetDynamicColumnsViewNameEnum.HotspotAnalysis
      )
      .then((response) => response.data ?? [])
      .catch((err) => {
        console.error(err, "error.fetchHotspotColumns");
        return [];
      });

    // Only SummaryTable numeric measures are rankable: restrict to computed
    // summary-table indicators (the hotspot `value` keys) and drop user-added
    // IFC/product properties as well as any non-numeric dimension columns.
    const options: SelectOption[] = columns
      .filter(
        (col) =>
          col.source === ColumnSourceEnum.SummaryTable && col.type === "NUMERIC"
      )
      .map((col) => ({
        id: String(col.id),
        value: col.key_name,
        name: col.short_with_unit ?? col.friendly_name,
        name_de: col.short_with_unit_de ?? col.friendly_name_de,
      }));

    const defaultColumn = columns.find((col) => col.default)?.key_name;

    runInAction(() => {
      // Bail if the building changed while columns were loading.
      if (this.currentBuildingID !== building_id) return;
      if (options.length) this.hotspotValueOptions = options;
      // Adopt the view's default (or first option) unless the current
      // selection is still valid.
      if (!options.some((o) => o.value === this.hotspotValueSelected)) {
        this.hotspotValueSelected =
          defaultColumn ?? options[0]?.value ?? this.hotspotValueSelected;
      }
    });
  }

  // One hotspot request; null on failure.
  private requestHotspot(
    building_id: string,
    value: string
  ): Promise<DashboardBuildingHotspotOUT | null> {
    return buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingHotspots(
        building_id,
        { value, top_n: this.hotspotTopN },
        getLocalAuthHeader()
      )
      .then((response) => response.data ?? null)
      .catch((err) => {
        console.error(err, "error.fetchHotspots");
        return null;
      });
  }

  async fetchHotspots(building_id: string | undefined): Promise<void> {
    if (!building_id) return;
    // Capture selection so a slow resolve can't clobber a newer one.
    const value = this.hotspotValueSelected;
    const data = await this.requestHotspot(building_id, value);
    runInAction(() => {
      if (this.currentBuildingID !== building_id) return;
      if (this.hotspotValueSelected !== value) return;
      // Settle to the result even on failure (null) — never show one
      // indicator's ranking under another's name.
      this.hotspotInfo = data;
    });
  }

  async fetchGwpIndicatorTotals(
    building_id: string | undefined
  ): Promise<void> {
    if (!building_id) return;
    const entries = await Promise.all(
      GWP_SUBINDICATOR_KEYS.map(
        async (key) =>
          [
            key,
            (await this.requestHotspot(building_id, key))?.total ?? null,
          ] as const
      )
    );
    runInAction(() => {
      if (this.currentBuildingID !== building_id) return;
      this.gwpIndicatorTotals = Object.fromEntries(entries);
    });
  }

  // Columns first (sets a valid indicator), then fetch the union of selected +
  // GWP keys once so the shared default indicator isn't requested twice.
  async init(building_id: string | undefined): Promise<void> {
    if (!building_id) return;
    await this.fetchHotspotColumns(building_id);

    const selected = this.hotspotValueSelected;
    const keys = Array.from(new Set([selected, ...GWP_SUBINDICATOR_KEYS]));
    const byKey = new Map(
      await Promise.all(
        keys.map(
          async (key) =>
            [key, await this.requestHotspot(building_id, key)] as const
        )
      )
    );

    runInAction(() => {
      if (this.currentBuildingID !== building_id) return;
      if (this.hotspotValueSelected === selected) {
        const selectedData = byKey.get(selected);
        if (selectedData) this.hotspotInfo = selectedData;
      }
      this.gwpIndicatorTotals = Object.fromEntries(
        GWP_SUBINDICATOR_KEYS.map((key) => [key, byKey.get(key)?.total ?? null])
      );
    });
  }

  reset() {
    runInAction(() => {
      this.hotspotInfo = null;
      this.hotspotValueOptions = [];
      this.hotspotValueSelected = DEFAULT_INDICATOR;
      this.gwpIndicatorTotals = {};
    });
  }

  static instance: LevelsDashboardStore;

  constructor() {
    makeAutoObservable(this);
  }

  static getInstance(): LevelsDashboardStore {
    if (!LevelsDashboardStore.instance) {
      LevelsDashboardStore.instance = new LevelsDashboardStore();
    }
    return LevelsDashboardStore.instance;
  }
}

export const levelsDashboardStore = LevelsDashboardStore.getInstance();
